import React, { useState, useEffect, useRef } from 'react';
import { Play, Square } from 'lucide-react';
import sampleAudio from './assets/sample-9s.mp3';
import logger from './utils/logger.js';


export default function SpeakerVisualizer({ setAudioQuality }) {
  const [audioStrength, setAudioStrength] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);
  const totalBars = width < 768 ? 25 : 50;
  const audioRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const audioContextRef = useRef(null);
  const intervalIdRef = useRef(null);

  const getBarColor = (index) => {
    if (index < audioStrength) {
      if (index < 8) return 'bg-red-500';
      if (index < 16) return 'bg-yellow-500';
      return 'bg-green-500';
    }
    return 'bg-gray-200';
  };

  const evaluateAudioQuality = (strength) => {
    if (strength < 8) {
      setAudioQuality("Weak");
    } else if (strength < 16) {
      setAudioQuality("Good");
    } else {
      setAudioQuality("Excellent");
    }
  };

  const handlePlay = async () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      if (!audioRef.current) {
        audioRef.current = new Audio(sampleAudio);
      }

      if (!analyserRef.current) {
        analyserRef.current = audioContextRef.current.createAnalyser();
        sourceRef.current = audioContextRef.current.createMediaElementSource(audioRef.current);
        sourceRef.current.connect(analyserRef.current);
        analyserRef.current.connect(audioContextRef.current.destination);
        analyserRef.current.fftSize = 256;
      }

      // Reset audio to start
      audioRef.current.currentTime = 0;
      await audioRef.current.play();
      setIsPlaying(true);

      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }

      intervalIdRef.current = setInterval(() => {
        analyserRef.current.getByteFrequencyData(dataArray);
        const strength = Math.floor(dataArray.reduce((a, b) => a + b) / bufferLength);
        const normalizedStrength = Math.floor((strength / 256) * totalBars);
        setAudioStrength(normalizedStrength);
        evaluateAudioQuality(normalizedStrength);
      }, 100);

      audioRef.current.onended = () => {
        setIsPlaying(false);
        clearInterval(intervalIdRef.current);
      };
    } catch (error) {
      logger.error('Error playing audio:', error);
    }
  };

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    clearInterval(intervalIdRef.current);
    setAudioStrength(0);
    setAudioQuality(""); // Reset audio quality
  };

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  return (
    <div className="flex flex-col items-center gap-2">
      <div className={`${width < 768 ? 'w-[330px]' : 'w-[500px]'} h-[20px] flex items-center gap-1`}>
        <div className="flex-1 h-full flex items-center gap-[1px]">
          {[...Array(totalBars)].map((_, index) => (
            <div
              key={index}
              className={`h-full flex-1 ${getBarColor(index)} transition-colors duration-300 rounded-md`}
              role="presentation"
              aria-hidden="true"
            />
          ))}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handlePlay}
            disabled={isPlaying}
            className={`flex items-center justify-center ${width < 768 ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm'} rounded-md transition-colors ${
              isPlaying
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-green-500 text-white hover:bg-green-600 active:bg-green-700'
            }`}
          >
            <Play className={`${width < 768 ? 'h-3 w-3' : 'h-4 w-4'} mr-1`} />
            Play
          </button>
          <button
            onClick={handleStop}
            disabled={!isPlaying}
            className={`flex items-center justify-center ${width < 768 ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm'} rounded-md transition-colors ${
              !isPlaying
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-red-500 text-white hover:bg-red-600 active:bg-red-700'
            }`}
          >
            <Square className={`${width < 768 ? 'h-3 w-3' : 'h-4 w-4'} mr-1`} />
            Stop
          </button>
        </div>
      </div>
    </div>
  );
}

